/**
 * Code component overrides for react-markdown 9.x.
 *
 * react-markdown 9 removed the `inline` prop. We distinguish inline code
 * from block code by inspecting the code node itself:
 *
 *   - Block code:  has a `language-*` className (from a fenced block) OR
 *                   its raw text contains a newline (multi-line inline code
 *                   gets promoted to block for correct rendering).
 *   - Inline code: everything else.
 *
 * This dispatch happens inside the `code` component — NOT the `pre`
 * component — because react-markdown transforms the `<code>` child via
 * our `components` map BEFORE `pre` ever sees it. By the time a `pre`
 * override runs, its child is already `React.createElement(OurCode)` and
 * a `ch.type === 'code'` check would never match. So the code component
 * itself does the dispatch.
 *
 * The `pre` component is a simple passthrough — the block code component
 * renders its own outer container (RichCodeBlock / StreamingCodeBlock),
 * so we don't want an extra `<pre>` wrapping it.
 */
import React from 'react';

const extractLanguage = (className) => {
  const match = /(?:lang|language)-(\w+)/.exec(className || '');
  return match ? match[1] : 'text';
};

/**
 * Flatten children to a raw string. For both fenced code blocks and inline
 * code, react-markdown delivers the content as a plain string or a single
 * text child, but we defensively handle nested shapes.
 */
const childrenToString = (children) => {
  if (typeof children === 'string') return children;
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(childrenToString).join('');
  if (children?.props?.children !== undefined) {
    return childrenToString(children.props.children);
  }
  if (children == null) return '';
  return String(children);
};

/**
 * Factory that returns a `code` component configured with a specific block
 * renderer. The same factory is called once per CellCogMarkdown instance.
 */
export const makeCodeComponent = ({
  CodeBlockComponent,
  defaultMode = 'raw',
  hideToggle = false,
}) =>
  React.memo(function Code({ className, children, node, ...props }) {
    const match = /(?:lang|language)-(\w+)/.exec(className || '');
    const hasLanguage = !!match;
    const rawText = childrenToString(children);
    // An empty code element is never intended content: stray fence lines (an unclosed fence, a fence pair around
    // nothing) rendered as empty grey bars in chat (Sep 23 2026). Structure only, never reads words.
    if (!rawText.trim()) return null;
    // Multi-line code without a language class also counts as a block —
    // markdown-to-jsx historically did this too, and it's what users expect
    // when pasting multi-line snippets with backticks.
    const isBlock = hasLanguage || /\n/.test(rawText);

    if (!isBlock) {
      return (
        <code className={`inline-code${className ? ` ${className}` : ''}`} {...props}>
          {children}
        </code>
      );
    }

    const language = hasLanguage ? match[1] : 'text';
    const code = rawText.replace(/\n$/, '');

    if (!CodeBlockComponent) {
      // No block renderer provided (e.g. MarkdownPreview inside a
      // RichCodeBlock — we intentionally avoid nesting RichCodeBlock
      // inside itself). Fall back to a vanilla pre/code pair.
      return (
        <pre className="cellcog-markdown-plain-pre">
          <code className={className || ''}>{code}</code>
        </pre>
      );
    }

    // RichCodeBlock accepts a hideToggle prop to suppress the Rich/Raw
    // segmented control — chat history uses this since the toggle adds
    // visual noise inside tight message bubbles. StreamingCodeBlock
    // ignores the prop (no toggle to hide), so the spread is harmless
    // there.
    return (
      <CodeBlockComponent
        code={code}
        language={language}
        defaultMode={defaultMode}
        hideToggle={hideToggle}
        className="cellcog-markdown-code-block"
      />
    );
  });

/**
 * `pre` passthrough. The block code component renders its own outer
 * container; we don't want the default `<pre>` wrapping it. Using a
 * fragment (via React.Fragment) avoids creating any DOM element for `pre`.
 */
export const PreFragment = React.memo(function Pre({ children }) {
  return <>{children}</>;
});

// Legacy export name kept for backward compatibility inside this package.
export const InlineCode = makeCodeComponent({ CodeBlockComponent: null });
export const makePreBlock = () => PreFragment;
